import { useState } from 'react';

import { Box, List, ListItem, Pagination, Tooltip, Typography } from '@mui/material';

import type { RelatedClientRecord } from '@/apps/engagement-letter-database/data/types';

const CLIENTS_PER_PAGE = 5;

interface SelectedClientsListProps {
  selectedClients: RelatedClientRecord['Clients'];
}

const SelectedClientsList = ({ selectedClients }: SelectedClientsListProps) => {
  const [clientPage, setClientPage] = useState(1);
  const pageCount = Math.ceil(selectedClients.length / CLIENTS_PER_PAGE);
  const page = Math.min(clientPage, Math.max(pageCount, 1));

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2">Selected Clients ({selectedClients.length})</Typography>
      <List aria-label="Selected Clients" dense disablePadding sx={{ mt: 1 }}>
        {selectedClients.slice((page - 1) * CLIENTS_PER_PAGE, page * CLIENTS_PER_PAGE).map((client) => (
          <ListItem key={client.ClientNumber} disableGutters sx={{ gap: 2, py: 0.5 }}>
            <Tooltip title={client.ClientName1 ?? client.ClientNumber}>
              <Typography variant="body2" noWrap sx={{ flex: 1, minWidth: 0 }}>
                {client.ClientName1 ?? client.ClientNumber}
              </Typography>
            </Tooltip>
            <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
              {client.ClientNumber}
            </Typography>
          </ListItem>
        ))}
      </List>
      {selectedClients.length > CLIENTS_PER_PAGE && (
        <Box
          sx={{
            mt: 1,
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1,
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography variant="caption" color="text.secondary">
            {(page - 1) * CLIENTS_PER_PAGE + 1}-{Math.min(page * CLIENTS_PER_PAGE, selectedClients.length)} of{' '}
            {selectedClients.length}
          </Typography>
          <Pagination
            aria-label="Selected clients pages"
            count={pageCount}
            page={page}
            onChange={(_event, nextPage) => setClientPage(nextPage)}
            size="small"
            siblingCount={0}
          />
        </Box>
      )}
    </Box>
  );
};

export default SelectedClientsList;
